<template>
  <div class="tab-content">
    <section class="settings-card">
      <div class="card-header">
        <div class="card-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 1l22 22"/><path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/><path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/><path d="M10.71 5.05A16 16 0 0 1 22.56 9"/><path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>
        </div>
        <div>
          <h3 class="card-title">Tempo Offline</h3>
          <p class="card-desc">Configure por quantos dias os usuários podem usar o sistema sem conexão com a internet.</p>
        </div>
      </div>

      <div class="offline-timeout-setting">
        <label class="field-label">Limite de dias offline</label>
        <div class="offline-timeout-row">
          <input class="field-input offline-timeout-input" type="number" min="0" :value="form.offlineTimeoutDays ?? 30"
            @input="$emit('update-form', { field: 'offlineTimeoutDays', value: String(($event.target as HTMLInputElement).value) })" />
          <span class="offline-timeout-hint">dias <span v-if="(form.offlineTimeoutDays ?? 30) === 0" class="offline-unlimited-badge">Ilimitado</span></span>
        </div>
        <p class="field-hint">Defina 0 para tempo ilimitado.</p>
      </div>

      <div class="save-row">
        <button class="btn-save" type="button" @click="$emit('save')">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
          Salvar
        </button>
        <Transition name="fade"><span v-if="successText" class="save-success">{{ successText }}</span></Transition>
        <p v-if="errorText" class="save-error">{{ errorText }}</p>
      </div>

      <div class="offline-users-section">
        <h4 class="offline-users-title">Status dos usuários</h4>
        <div v-if="loading" class="offline-users-loading">Carregando...</div>
        <table v-else class="offline-users-table">
          <thead><tr><th>Nome</th><th>Usuário</th><th>Última conexão</th><th>Dias offline</th><th>Status</th></tr></thead>
          <tbody>
            <tr v-for="u in userRows" :key="u.username">
              <td>{{ u.name }}</td>
              <td><code>{{ u.username }}</code></td>
              <td>{{ u.lastOnlineFmt }}</td>
              <td>{{ u.daysOffline !== null ? u.daysOffline : '—' }}</td>
              <td><span class="offline-status-pill" :class="u.statusClass">{{ u.statusLabel }}</span></td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, type PropType } from 'vue'
import type { SystemConfig } from '@/store/system'
import { accountsStore } from '@/store/accounts'

export default defineComponent({
  name: 'SystemTabOffline',
  props: { form: { type: Object as PropType<SystemConfig>, required: true }, errorText: { type: String, default: '' }, successText: { type: String, default: '' } },
  emits: ['update-form', 'save'],
  setup(props) {
    const loading = ref(false)
    onMounted(async () => {
      if (accountsStore.state.items.length === 0) {
        loading.value = true
        try { await accountsStore.refresh() } finally { loading.value = false }
      }
    })
    const userRows = computed(() => {
      const timeout = props.form.offlineTimeoutDays ?? 30
      const now = Date.now()
      return accountsStore.state.items.map((u: any) => {
        const lastRaw = u.lastOnlineAt || ''
        const lastMs = lastRaw ? new Date(lastRaw).getTime() : NaN
        const daysOffline = isNaN(lastMs) ? null : Math.floor((now - lastMs) / 86_400_000)
        const lastOnlineFmt = !lastRaw ? 'Nunca' : new Date(lastRaw).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
        let statusLabel = 'Online recente', statusClass = 'pill--ok'
        if (daysOffline === null) { statusLabel = 'Desconhecido'; statusClass = 'pill--unknown' }
        else if (timeout > 0 && daysOffline >= timeout) { statusLabel = 'Bloqueado'; statusClass = 'pill--blocked' }
        else if (daysOffline >= 7) { statusLabel = `${daysOffline}d offline`; statusClass = 'pill--warn' }
        return { username: u.username, name: u.name, lastOnlineFmt, daysOffline, statusLabel, statusClass }
      })
    })
    return { loading, userRows }
  }
})
</script>

<style scoped lang="scss">
.tab-content { display: flex; flex-direction: column; gap: 16px; }
.settings-card { border: 1px solid rgba(31,30,26,.1); border-radius: 14px; background: var(--card,#fff); padding: 22px 24px; display: flex; flex-direction: column; gap: 20px; }
.card-header { display: flex; align-items: flex-start; gap: 14px; padding-bottom: 16px; border-bottom: 1px solid rgba(31,30,26,.07); }
.card-icon { width: 36px; height: 36px; border-radius: 9px; flex-shrink: 0; background: rgba(31,30,26,.06); display: flex; align-items: center; justify-content: center; }
.card-title { margin: 0 0 2px; font-size: 15px; font-weight: 700; color: var(--ink,#2c2b27); }
.card-desc { margin: 0; font-size: 12px; color: var(--ink-muted,#888); }
.field-label { font-size: 11px; font-weight: 700; color: var(--ink-muted,#888); text-transform: uppercase; letter-spacing: .05em; }
.field-hint { font-size: 11px; color: var(--ink-muted,#aaa); margin: 0; }
.field-input { padding: 9px 12px; border-radius: 8px; border: 1.5px solid rgba(31,30,26,.16); font-size: 14px; color: var(--ink,#2c2b27); background: #fff; outline: none; box-sizing: border-box; width: 100%; &:focus { border-color: var(--system-navbar,#0f4c81); } }
.offline-timeout-setting { display: flex; flex-direction: column; gap: 6px; max-width: 320px; }
.offline-timeout-row { display: flex; align-items: center; gap: 10px; }
.offline-timeout-input { width: 100px !important; }
.offline-timeout-hint { font-size: 13px; color: var(--ink-muted,#6b7280); display: flex; align-items: center; gap: 6px; }
.offline-unlimited-badge { background: #dbeafe; color: #1e40af; font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 20px; }
.save-row { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.btn-save { display: inline-flex; align-items: center; gap: 8px; padding: 11px 24px; border-radius: 10px; border: none; cursor: pointer; background: var(--system-navbar,#0f4c81); color: #fff; font-size: 14px; font-weight: 700; &:hover { opacity: .88; } }
.save-success { font-size: 13px; font-weight: 600; color: #15803d; }
.save-error { margin: 0; font-size: 13px; color: #dc2626; }
.offline-users-section { border-top: 1px solid rgba(31,30,26,.08); padding-top: 20px; }
.offline-users-title { font-size: 14px; font-weight: 600; color: var(--ink,#111827); margin: 0 0 14px; }
.offline-users-loading { font-size: 14px; color: var(--ink-muted,#6b7280); }
.offline-users-table { width: 100%; border-collapse: collapse; font-size: 13px; th { text-align: left; padding: 8px 12px; font-size: 11px; font-weight: 600; color: var(--ink-muted,#6b7280); text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid rgba(31,30,26,.1); } td { padding: 10px 12px; color: var(--ink,#374151); border-bottom: 1px solid #f3f4f6; vertical-align: middle; } tr:last-child td { border-bottom: none; } code { font-size: 12px; background: #f3f4f6; padding: 2px 6px; border-radius: 4px; } }
.offline-status-pill { font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 20px; &.pill--ok { background: #dcfce7; color: #166534; } &.pill--warn { background: #fef3c7; color: #92400e; } &.pill--blocked { background: #fef2f2; color: #dc2626; } &.pill--unknown { background: #f3f4f6; color: #6b7280; } }
.fade-enter-active, .fade-leave-active { transition: opacity .3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
